import { canChange, FIELD_TEXT, fieldFixText, otherFixText, type RefusedField, refusedField, STOP_PICKUP, syncFixText } from "./offline/messages";
import { type OutboxEntry, SECOND_PICKUP, stateText } from "./offline/outbox";
import { OUTCOMES, type Outcome, type OutcomeField, PARTIAL_TEXT } from "./today";
import type { RouteStop } from "./api";
import { gallonsText } from "./pickup";
import { asksLeft, type VisitForm } from "./visits";

/** The element of Record a visit that gets the focus for each refused field. */
export const FIELD_FOCUS: Record<RefusedField, string> = {
  gallons: "visit-gallons",
  scrap: "visit-scrap",
  left: "visit-left",
  reason: "visit-reason",
  time: "visit-day",
  truck: "visit-truck",
  driver: "visit-driver",
  account: "visit-change-account",
};

export interface Reopen {
  form: VisitForm;
  note: string;
  error: { field: RefusedField; text: string } | null;
  focusId: string | null;
}

const shows = (field: RefusedField, form: VisitForm) =>
  field === "gallons" || field === "scrap" ? !form.nothing : field === "left" ? asksLeft(form) : field === "reason" ? form.nothing !== null : true;

/** How the form shows a refused visit that the driver puts back: the field that the server named, or only the status line. */
export function reopenView(entry: OutboxEntry, entryForm: VisitForm, word: string): Reopen {
  const named = entry.field !== undefined ? entry.field : refusedField(0, entry.detail);
  const form = named === "left" && !entryForm.nothing ? { ...entryForm, notEmpty: true } : entryForm;
  const field = named && shows(named, form) ? named : null;
  const intro = `This entry is in the fields below: ${entry.label}.`;
  return {
    form,
    note: field ? intro : `${intro} ${entry.reason ?? ""}`.trim(),
    error: field ? { field, text: fieldFixText(field, word) } : null,
    focusId: field ? FIELD_FOCUS[field] : null,
  };
}

/** The refused visits that the form and the status line do not show yet, and the newest of them. */
export interface Refused {
  newest: OutboxEntry;
  count: number;
}

export function refusedVisits(all: readonly OutboxEntry[] | null, shown: readonly (string | null | undefined)[]): Refused | null {
  const found = (all ?? []).filter((entry) => entry.kind === "visit" && entry.state === "failed" && !shown.includes(entry.id));
  if (!found.length) return null;
  const newest = found.reduce((top, entry) => (entry.created_at > top.created_at ? entry : top));
  return { newest, count: found.length };
}

/** The status line of the refused visits after a reload (SO-1006, SO-1007). */
export function refusedText(refused: Refused): string {
  const one = `${refused.newest.label}. ${refused.newest.reason ?? ""}`.trim();
  return refused.count === 1 ? `Not saved: ${one}` : `${refused.count} visits are not saved. The newest: ${one}`;
}

export const moreRefusedText = (count: number) => (count === 1 ? "1 more visit is not saved." : `${count} more visits are not saved.`);

/** True when the visit form has values that the driver typed and did not save. */
export const visitDraft = (form: VisitForm) =>
  form.gallons.trim() !== "" ||
  form.scrap.trim() !== "" ||
  form.left.trim() !== "" ||
  form.nothing !== null ||
  form.notEmpty ||
  form.labels.length > 0 ||
  form.note.trim() !== "";

/** The question before "Change this entry" puts a refused entry over typing that is not saved (SO-1008). */
export const DRAFT_ASK = "The form has values that you did not save. If you change this entry, the form forgets them.";
export const DRAFT_KEEP = "Keep my values";

/** The fields of a Today stop form. */
export interface StopFields {
  text: string;
  scrap: string;
  pickupSaved: boolean;
  outcome: Outcome | null;
  choice: string;
  other: string;
  left: string;
}

export interface StopReopen {
  fields: StopFields;
  at: OutcomeField | null;
  error: string | null;
  focusId: string | null;
}

/** The newest pickup entry of the account on the route: a Done stop sends its pickup first. */
export const stopPickup = (all: readonly OutboxEntry[], routeId: string | null, accountId: string) =>
  all
    .filter((entry) => entry.kind === "pickup" && entry.route_id === routeId && entry.path === `/accounts/${encodeURIComponent(accountId)}/pickups`)
    .reduce<OutboxEntry | undefined>((top, entry) => (!top || entry.created_at > top.created_at ? entry : top), undefined);

/** True when the pickup is on the phone, on the server, or refused because the server has a copy: a second pickup must not go. */
export const pickupHeld = (pickup: OutboxEntry | undefined): pickup is OutboxEntry => !!pickup && (pickup.state !== "failed" || !canChange(pickup.reason));

/** The save button of a Today stop form. */
export const stopButton = (outcome: Outcome | null, pickupSaved: boolean) =>
  outcome ? OUTCOMES[outcome].confirm : pickupSaved ? "Mark it done" : "Save and mark done";

export const PICKUP_SAVED_TIME = "The pickup is saved. The stop is not marked done yet: its time was not correct. Tap Mark it done to save it with the time now.";
export const PICKUP_SAVED_AGAIN = "The pickup is saved. The stop is not marked done yet. Tap Mark it done.";

/** The line of a Done stop that the server refused after its pickup went (SO-1012). Its gallons cannot change. */
export function pickupSavedText(refused: Pick<OutboxEntry, "reason" | "field" | "detail"> | null): string {
  if (!refused) return "The pickup is saved. The stop is not marked done yet.";
  const field = refused.field !== undefined ? refused.field : refusedField(0, refused.detail);
  if (field === "time") return PICKUP_SAVED_TIME;
  if (canChange(refused.reason)) return PICKUP_SAVED_AGAIN;
  return `The pickup is saved. The stop is not marked done: ${refused.reason ?? ""}`.trim();
}

const numberText = (value: unknown) => (typeof value === "number" ? String(value) : "");

type SavedPickup = NonNullable<RouteStop["saved_pickup"]>;

/** The line of a planned stop whose pickup the server has (SO-1080, SO-1110), for example from a different phone. */
export const serverPickupText = (saved: SavedPickup) =>
  `The pickup is saved: ${saved.raw_gal} gal total, ${saved.scrap_gal} gal scrap. Tap Mark it done, or Partly emptied if gallons are left. To change the pickup, speak to the office.`;

/** The pickup gallons that the server has for a stop: the saved pickup of a planned stop, or the result of a done stop. */
export function serverGallons(stop: Pick<RouteStop, "state" | "saved_pickup" | "raw_gal" | "scrap_gal">): SavedPickup | null {
  if (stop.state === "planned") return stop.saved_pickup ?? null;
  return stop.raw_gal != null ? { id: "", raw_gal: stop.raw_gal, scrap_gal: stop.scrap_gal ?? 0 } : null;
}

/** True when the server kept a pickup of a different phone for the stop, so the gallons of this pickup are lost (SO-1106). */
export const otherPickupKept = (pickup: Pick<OutboxEntry, "kind" | "copy" | "detail"> | undefined) =>
  pickup?.kind === "pickup" && pickup.copy === true && STOP_PICKUP.test(pickup.detail ?? "");

/** The top line of a Done stop whose pickup the server refused: the server gallons, not the typed ones (SO-1106). */
export function otherPickupText(number: string, pickup: Pick<OutboxEntry, "body">, saved: SavedPickup | null): string {
  const typed = numberText(pickup.body.raw_gal);
  const lost = typed ? `The server did not keep your ${typed} gal.` : "The server did not keep your gallons.";
  if (!saved) return `${number} is done. The server had a pickup for this stop before. ${lost} If the saved gallons are not correct, speak to the office.`;
  const kept = gallonsText({ raw: saved.raw_gal, scrap: saved.scrap_gal });
  if (typed === String(saved.raw_gal) && numberText(pickup.body.scrap_gal) === String(saved.scrap_gal))
    return `${number} is done. The server had a pickup for this stop before, with the same gallons: ${kept}.`;
  return `${number} is done. The server had a pickup for this stop before: ${kept}. ${lost} If ${saved.raw_gal} gal is not correct, speak to the office.`;
}

/** The Today text of a stop entry with gallons that the server refused, because the stop has a pickup (SO-1083, SO-1109). */
export function stopPickupText(saved: SavedPickup | null): string {
  if (!saved) return "The server has a pickup for this stop. It did not keep the gallons of this entry. Tap Mark it done or Partly emptied for this stop.";
  const kept = gallonsText({ raw: saved.raw_gal, scrap: saved.scrap_gal });
  return `The server has a pickup for this stop: ${kept}. It did not keep the gallons of this entry. Tap Mark it done or Partly emptied for this stop. If ${saved.raw_gal} gal is not correct, speak to the office.`;
}

/** The Today text of a refused entry, with the server gallons for a stop pickup refusal (SO-1109). */
export const todayReason = (reason: string, saved: SavedPickup | null) => (reason === FIELD_TEXT.stopPickup ? stopPickupText(saved) : reason);

const RESULT_WORD: Record<Exclude<RouteStop["state"], "planned">, string> = {
  done: "done",
  partial: "partly emptied",
  skipped: "skipped",
  measured: "measured",
  no_access: "marked no access",
};

/** True when the day shows a result for the stop of this refused first entry: a different phone or the office saved it (SO-1107). */
export const doneElsewhere = (entry: Pick<OutboxEntry, "kind" | "state" | "body">, stop: Pick<RouteStop, "state"> | undefined) =>
  firstRefused(entry) && !!stop && stop.state !== "planned";

/** A refused stop entry that is not a change. */
const firstRefused = (entry: Pick<OutboxEntry, "kind" | "state" | "body">) => entry.kind === "stop" && entry.state === "failed" && entry.body.change_id === undefined;

/** The text of a refused first entry whose stop has a result on the server; Discard is the action (SO-1107). */
export const doneElsewhereText = (number: string, state: Exclude<RouteStop["state"], "planned">, where: "today" | "sync") =>
  `${number} is ${RESULT_WORD[state]} on the server, from a different phone or the office. This entry is not necessary. ${where === "sync" ? "Tap Discard." : "On Sync, tap Discard."}`;

/** The gallons of a pickup that the server has, for the fields that cannot change. */
export const savedGallons = (saved: SavedPickup) => ({ text: String(saved.raw_gal), scrap: String(saved.scrap_gal) });

/** The gallons of a held pickup, for the fields that cannot change. */
export const heldGallons = (pickup: OutboxEntry) => ({ text: numberText(pickup.body.raw_gal), scrap: numberText(pickup.body.scrap_gal) });

/** The line after a second pickup for a stop is blocked: it names the gallons that are saved. */
export function secondPickupText(held: OutboxEntry | undefined): string {
  const { text, scrap } = held ? heldGallons(held) : { text: "", scrap: "" };
  if (!text) return SECOND_PICKUP;
  return `The pickup of this stop is saved already with ${text} gal total, ${scrap || "0"} gal scrap. The phone did not save the gallons again. Tap Mark it done.`;
}

/** How a Today stop form shows a refused stop entry that the driver puts back (SO-1009, SO-1011). */
export function stopReopen(entry: OutboxEntry, pickup: OutboxEntry | undefined): StopReopen {
  const body = entry.body as { state?: string; reason?: string; gallons_left?: number; raw_gal?: number; scrap_gal?: number };
  const outcome = body.state && body.state in OUTCOMES ? (body.state as Outcome) : null;
  const done = body.state === "done";
  const reason = body.reason ?? "";
  const reasons = outcome ? OUTCOMES[outcome].reasons : [];
  const named = outcome === "partial" ? (PARTIAL_TEXT[reason] ?? "") : reason;
  const choice = !reason ? "" : reasons.includes(named) ? named : reasons.includes("Other") ? "Other" : "";
  const partial = outcome === "partial" && body.raw_gal !== undefined;
  const pickupSaved = done && pickupHeld(pickup);
  const lostPickup = done && pickup?.state === "failed" && canChange(pickup.reason);
  const gallons = partial ? { text: numberText(body.raw_gal), scrap: numberText(body.scrap_gal) } : done && pickup && (pickupSaved || lostPickup) ? heldGallons(pickup) : { text: "", scrap: "" };
  const fields: StopFields = {
    ...gallons,
    pickupSaved,
    outcome,
    choice,
    other: choice === "Other" ? reason : "",
    left: numberText(body.gallons_left),
  };
  const field = entry.field !== undefined ? entry.field : refusedField(0, entry.detail);
  const at: OutcomeField | null =
    (field === "gallons" || field === "scrap") && !pickupSaved && (done || partial)
      ? field
      : field === "left" && outcome && OUTCOMES[outcome].reads
        ? "left"
        : field === "reason" && outcome
          ? choice === "Other"
            ? "other"
            : "reason"
          : null;
  const button = stopButton(outcome, pickupSaved);
  return {
    fields,
    at,
    error: at === "other" ? otherFixText(button) : at && field ? fieldFixText(field, "gallons", { button }) : null,
    focusId: at === "gallons" || at === "scrap" ? `${entry.id}-${at}` : at ? `${at}-${entry.id}` : null,
  };
}

/** True when the stop form has values that the driver typed and did not save. */
export const stopDraft = (fields: Pick<StopFields, "text" | "scrap" | "left" | "outcome" | "choice" | "other">) =>
  [fields.text, fields.scrap, fields.left, fields.choice, fields.other].some((value) => value.trim() !== "") || fields.outcome !== null;

/** The stop form of a refused change: its own values, never a held pickup (SO-1037). */
export function changeReopen(entry: OutboxEntry): StopReopen {
  const view = stopReopen(entry, undefined);
  const body = entry.body as { state?: string; raw_gal?: number; scrap_gal?: number };
  if (body.state !== "done" || body.raw_gal === undefined) return view;
  return { ...view, fields: { ...view.fields, text: numberText(body.raw_gal), scrap: numberText(body.scrap_gal) } };
}

/** The Sync text of a Not saved entry: done elsewhere, the time text of a Today entry, or its reason (SO-1107, SO-1108). */
export function syncEntryText(entry: Pick<OutboxEntry, "kind" | "state" | "body" | "reason">, stop: Pick<RouteStop, "state" | "number"> | undefined, fixedOnToday: boolean): string {
  if (stop && stop.state !== "planned" && doneElsewhere(entry, stop)) return doneElsewhereText(stop.number, stop.state, "sync");
  const reason = entry.reason ?? "";
  return fixedOnToday ? syncFixText(reason, entry.body.change_id !== undefined) : reason;
}

/** The Sync line of a pickup that the server refused for a pickup of a different phone (SO-1112). */
export const OTHER_PICKUP_NOTE = "The server has a different pickup for this stop. It did not keep these gallons.";

/** True when a Sync row needs the server day: a refused first stop entry, or a pickup that the server did not keep (SO-1116). */
export const needsServerDay = (all: readonly OutboxEntry[]) => all.some((entry) => firstRefused(entry) || otherPickupKept(entry));

/** The Sync text of a refused first stop entry while the phone gets the server day (SO-1117). */
export const DAY_CHECK_TEXT = "The phone checks this stop with the server.";

/** Sync shows Fix on Today after the server day came, or when it cannot come; never for a stop done elsewhere (SO-1117). */
export const syncFixShown = (entry: Pick<OutboxEntry, "kind" | "state" | "body">, stop: Pick<RouteStop, "state"> | undefined, fixedOnToday: boolean, dayWait: boolean) =>
  fixedOnToday && !doneElsewhere(entry, stop) && !(dayWait && firstRefused(entry));

/** The Sync text of a Not saved entry, also while the server day loads (SO-1117). */
export const syncRowText = (entry: Pick<OutboxEntry, "kind" | "state" | "body" | "reason">, stop: Pick<RouteStop, "state" | "number"> | undefined, fixedOnToday: boolean, dayWait: boolean) =>
  dayWait && firstRefused(entry) && !doneElsewhere(entry, stop) ? DAY_CHECK_TEXT : syncEntryText(entry, stop, fixedOnToday && !doneElsewhere(entry, stop));

/** The Discard confirm of a Sync row (SO-1118). */
export function discardText(entry: Pick<OutboxEntry, "kind" | "state" | "body" | "label">, stop: Pick<RouteStop, "state"> | undefined): string {
  if (doneElsewhere(entry, stop)) return `Discard "${entry.label}"? The server already has a result for this stop from a different phone. The phone then forgets this entry.`;
  return `Discard "${entry.label}"? The server does not have it, and the phone then ${entry.kind === "photo" ? "removes the photo" : "forgets it"}.`;
}

/** The Sync chip of a pickup that the server did not keep (SO-1119). */
export const NOT_KEPT = "Not kept";

/** The Sync chip text and class: a pickup that the server did not keep is "Not kept" in the colour of Not saved (SO-1119). */
export const syncChip = (entry: Pick<OutboxEntry, "kind" | "state" | "copy" | "detail">) =>
  otherPickupKept(entry) ? { text: NOT_KEPT, tone: "failed" as const } : { text: stateText(entry), tone: entry.state };

/** The Sync label of a sent Done stop whose pickup the server did not keep: the server gallons, not the typed ones (SO-1120). */
export function syncStopLabel(entry: OutboxEntry, all: readonly OutboxEntry[], stop: Pick<RouteStop, "number" | "account_id" | "state" | "saved_pickup" | "raw_gal" | "scrap_gal"> | undefined): string {
  if (entry.kind !== "stop" || entry.state !== "sent" || entry.body.change_id !== undefined || entry.body.state !== "done" || !stop) return entry.label;
  if (!otherPickupKept(stopPickup(all, entry.route_id, stop.account_id))) return entry.label;
  const saved = serverGallons(stop);
  if (!saved) return `${stop.number} is done. The server has a different pickup for this stop.`;
  return `${stop.number} is done (server: ${gallonsText({ raw: saved.raw_gal, scrap: saved.scrap_gal })}).`;
}

/** The Today line of a saved pickup, hidden while the refusal line for a stop pickup gives the gallons (SO-1121). */
export const pickupSavedShown = (pickupSaved: boolean, doneHeld: boolean, refused: Pick<OutboxEntry, "state" | "reason"> | undefined) =>
  pickupSaved && !doneHeld && !(refused?.state === "failed" && refused.reason === FIELD_TEXT.stopPickup);

/** The form that a forced reload for a new version keeps (rule U-35). */
export interface KeptForm<T> {
  target: T;
  form: VisitForm;
  timeSet: boolean;
  at: number;
}

export const KEPT_FORM = "visit-form-kept";
export const KEPT_FORM_MS = 12 * 3_600_000;

/** The kept form when it is less than 12 h old and has typed values, else null. */
export function keptForm<T>(kept: KeptForm<T> | null | undefined, now: number): KeptForm<T> | null {
  if (!kept || typeof kept !== "object" || !kept.form || !kept.target) return null;
  if (now - kept.at > KEPT_FORM_MS || now < kept.at - 60_000) return null;
  return visitDraft(kept.form) ? kept : null;
}
